import React from "react";
import { useTenant } from "@hooks";
import { makeStyles, TableCell as TableCellBase, TableRow } from "@material-ui/core";
import { ThemeType } from "@theme";
import { Record } from "@config_editor/ContractsPriceTemplates/utils";
import locale from "@bonliva-crm/locale";
import { PriceListDay, PriceListWorkType } from "@graphql";
import { PayPeriod } from "@bonliva-crm/graphql";
import {
  applyNationalProcurementPriceStrategy,
  ContractPensionModel,
  getConsultantContractRecordPrice
} from "@bonliva-crm/shared";

type Props = {
  record: Record;
  price?: number;
};

const TableCell = (props) => <TableCellBase component={"div"} {...props} />;

export const getPrice = (
  tenant: string,
  basic: number,
  multiplier: number | null,
  extra: number | null,
  record,
  custom?: number | null,
  isNationalProcurementPriceStrategy: boolean = false,
  overLimit: boolean = false
) => {
  const suffix = `${locale.currency[record.currency]}/${
    locale.contractsPrice.payUnit[record.paidPer]
  }`;

  // HERE HACK, right now annual is default
  const price = getConsultantContractRecordPrice(
    ContractPensionModel.Annual,
    basic,
    multiplier,
    extra,
    custom,
    isNationalProcurementPriceStrategy,
    overLimit
  );

  return price != null ? `${price.toFixed(2)} ${suffix}` : "";
};

export const getCustomPrice = (
  tenant: string,
  customPrice: number,
  record,
  isNationalProcurementPriceStrategy: boolean = false,
  overLimit: boolean
) => {
  const suffix = `${locale.currency[record.currency]}/${
    locale.contractsPrice.payUnit[record.paidPer]
  }`;

  const price = applyNationalProcurementPriceStrategy(
    ContractPensionModel.Annual,
    customPrice,
    isNationalProcurementPriceStrategy,
    overLimit
  );

  return price != null ? `${price.toFixed(2)} ${suffix}` : "";
};

export const getFixedPrice = (
  tenant: string,
  fixedPrice: number,
  record,
  isNationalProcurementPriceStrategy: boolean,
  overLimit: boolean
) => {
  const suffix = `${locale.currency[record.currency]}`;

  const price = applyNationalProcurementPriceStrategy(
    ContractPensionModel.Annual,
    fixedPrice,
    isNationalProcurementPriceStrategy,
    overLimit
  );

  return price != null ? `${price.toFixed(2)} ${suffix}` : `0 ${suffix}`;
};

export const PriceRecordOutput = ({ record, price }: Props) => {
  const classes = useStyles({});
  const { tenant } = useTenant();

  return (
    <TableRow className={classes.wrap} component={"div"}>
      <TableCell>{record.articleNumber}</TableCell>
      <TableCell>{record.articleNumberForOtherSpeciality}</TableCell>
      <TableCell>{locale.contractsPrice.payPeriod[PayPeriod[record.period]]}</TableCell>
      <TableCell>
        {locale.contractsPrice.priceListWorkType[PriceListWorkType[record.workType]]}
      </TableCell>
      <TableCell align={"right"}>
        {record.period === PayPeriod.Daily &&
          `${
            record.startDay
              ? locale.contractsPrice.priceListDay[PriceListDay[record.startDay]]
              : ""
          } ${
            record.endDay
              ? " - " + locale.contractsPrice.priceListDay[PriceListDay[record.endDay]]
              : ""
          }`}
        {record.period === PayPeriod.FullPeriod &&
          `${
            record.startDay
              ? locale.contractsPrice.priceListDay[PriceListDay[record.startDay]]
              : ""
          }`}
      </TableCell>
      <TableCell style={{ width: 20 }}>{record.from}</TableCell>
      <TableCell style={{ width: 10, padding: 0 }}>{"--"}</TableCell>
      <TableCell align="justify">
        {record.period === PayPeriod.FullPeriod && (
          <div className={classes.end}>
            {record.endDay && (
              <span>
                {locale.contractsPrice.priceListDay[PriceListDay[record.endDay]]}
              </span>
            )}
            <span>{record.to}</span>
          </div>
        )}
        {record.period === PayPeriod.Daily && record.to}
      </TableCell>
      <TableCell align="right">
        {!record.customPrice && (
          <>
            {!record.fixedPrice
              ? record.priceMultiplier && `${record.priceMultiplier}x`
              : `+${record.fixedPrice}`}
          </>
        )}
      </TableCell>
      <TableCell align="right">
        {getPrice(
          tenant,
          price,
          record.priceMultiplier,
          record.fixedPrice,
          record,
          record.customPrice
        )}
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  wrap: {
    fontSize: "1.2em",
    "& div": {
      borderBottom: "none"
    }
  },
  end: {
    "& span:not(:first-child)": {
      paddingLeft: 20
    }
  }
}));
